import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { prepareImageSearchSrc } from "../../lib/downscale-image";
import { useImageSearchFlow } from "../../lib/image-search-flow";
import { stashSearchImage } from "../../lib/image-search-session";
import { CropModal } from "./CropModal";
import { UploadModal } from "./UploadModal";

/**
 * Single mount-point for the Mini image-search flow (upload → crop). Mounted at
 * the router root so any camera entry-point just calls
 * `useImageSearchFlow().openUpload(channel)`. Port of
 * apps/web/components/image-search/image-search-modals.tsx.
 *
 * On crop confirm we stash the cropped base64 under a per-search token and
 * navigate to /catalog?imageSearch=<token>&channel=<targetChannel> — the catalog
 * uploads it per channel there (upload never blocks navigation; web issue #118).
 */
export function ImageSearchModals() {
  const navigate = useNavigate();
  const phase = useImageSearchFlow((s) => s.phase);
  const src = useImageSearchFlow((s) => s.src);
  const cropContext = useImageSearchFlow((s) => s.cropContext);
  const targetChannel = useImageSearchFlow((s) => s.targetChannel);
  const openCrop = useImageSearchFlow((s) => s.openCrop);
  const close = useImageSearchFlow((s) => s.close);

  // Upload-step preview: the prepared (downscaled) data-URL + a "preparing" flag
  // so UploadModal can show a loading spinner while the file decodes.
  const [previewSrc, setPreviewSrc] = useState("");
  const [preparing, setPreparing] = useState(false);
  const prepareSeq = useRef(0);

  // Reset upload-step preview whenever we leave the upload phase.
  useEffect(() => {
    if (phase !== "upload") {
      setPreviewSrc("");
      setPreparing(false);
    }
  }, [phase]);

  // Escape closes the active modal.
  useEffect(() => {
    if (phase === "idle") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, close]);

  const onPicked = useCallback((file: File) => {
    const seq = ++prepareSeq.current;
    setPreparing(true);
    void (async () => {
      try {
        const prepared = await prepareImageSearchSrc(file);
        if (seq !== prepareSeq.current) return;
        setPreviewSrc(prepared);
      } finally {
        if (seq === prepareSeq.current) setPreparing(false);
      }
    })();
  }, []);

  const onUploadConfirm = useCallback(() => {
    if (!previewSrc) return;
    openCrop(previewSrc);
  }, [previewSrc, openCrop]);

  if (phase === "idle") return null;

  if (phase === "upload") {
    return (
      <UploadModal
        previewSrc={previewSrc}
        preparing={preparing}
        onPicked={onPicked}
        onConfirm={onUploadConfirm}
        onCancel={() => {
          prepareSeq.current += 1;
          close();
        }}
      />
    );
  }

  // phase === "crop"
  if (!src) return null;
  return (
    <CropModal
      src={src}
      initialContext={cropContext}
      onConfirm={(croppedBase64, context) => {
        const token = stashSearchImage(croppedBase64, context);
        close();
        navigate({
          to: "/catalog" as never,
          search: { imageSearch: token, channel: targetChannel } as never,
        });
      }}
      onCancel={() => close()}
    />
  );
}
